// import ErrorBoundary from 'generic/ErrorBoundary';
import Script from 'next/script';
import Head from 'next/head';
import { SpeedInsights } from '@vercel/speed-insights/next';

import { AdDataProvider } from 'utils/ad-data-context';
import { OrderDataProvider } from 'utils/order-data-context';
import { RemoteConfigProvider } from 'utils/feature-flags';
import { reportError } from 'utils/report-error';

import './styles/globals.css';
import './styles/contentModule.css';
import './styles/errorPage.css';

const IS_SERVER = typeof window === 'undefined';
if (!IS_SERVER) {
  window.addEventListener('unhandledrejection', event => reportError(event.reason));
  window.addEventListener('error', event => reportError(event.error));
}

function App({ Component, pageProps, err }) {
  const {
    rootDomain,
    googleOptimizeId,
    googleTagManagerId,
    loadFromServerContainer,
    googleTagManagerAuthCode,
    googleTagManagerPreview,
    serverFlags, // Server-side flags passed from getStaticProps/getServerSideProps
  } = pageProps.funnelData || {};

  const tagDomain = loadFromServerContainer ? `collect.${rootDomain}` : 'www.googletagmanager.com';

  const renderGoogleTagManagerScript = () => (
    <Script id="google-tag-manager" strategy="afterInteractive">
      {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://${tagDomain}/gtm.js?id='+i+dl${
        googleTagManagerAuthCode && googleTagManagerPreview
          ? `+'&gtm_auth=${googleTagManagerAuthCode}&gtm_preview=${googleTagManagerPreview}&gtm_cookies_win=x'`
          : ''
      };f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${googleTagManagerId}');`}
    </Script>
  );

  return (
    <>
      <Head>
        <link rel="preconnect" href="https://www.googletagmanager.com" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />

        <link rel="dns-prefetch" href="//www.googleoptimize.com" />
        <link rel="dns-prefetch" href="//connect.facebook.net" />
        <link rel="dns-prefetch" href="https://js.stripe.com" />
        <link rel="dns-prefetch" href="https://api.rudderlabs.com" />
        <link rel="dns-prefetch" href="https://cdn.rudderlabs.com" />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      </Head>
      <AdDataProvider>
        {!!rootDomain && (
          <>
            {process.env.NEXT_PUBLIC_DISABLE_GOOGLE_OPTIMIZE !== 'true' && googleOptimizeId && (
              <Script
                id="google-optimize"
                src={`https://www.googleoptimize.com/optimize.js?id=${googleOptimizeId}`}
              />
            )}

            {/* Google Tag Manager */}
            {process.env.NEXT_PUBLIC_DISABLE_GOOGLE_TAG_MANAGER !== 'true' &&
              renderGoogleTagManagerScript()}
          </>
        )}
        {/* Rudderstack */}
        {process.env.NEXT_PUBLIC_RUDDERSTACK_FEATURE_FLAG &&
          process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY && (
            <Script id="rudderstack">
              {`
!function(){"use strict";window.RudderSnippetVersion="3.0.60";var e="rudderanalytics";window[e]||(window[e]=[])
  ;var rudderanalytics=window[e];if(Array.isArray(rudderanalytics)){
  if(true===rudderanalytics.snippetExecuted&&window.console&&console.error){
  console.error("RudderStack JavaScript SDK snippet included more than once.")}else{rudderanalytics.snippetExecuted=true,
  window.rudderAnalyticsBuildType="legacy";var sdkBaseUrl="https://cdn.rudderlabs.com";var sdkVersion="v3"
  ;var sdkFileName="rsa.min.js";var scriptLoadingMode="async"
  ;var r=["setDefaultInstanceKey","load","ready","page","track","identify","alias","group","reset","setAnonymousId","startSession","endSession","consent"]
  ;for(var n=0;n<r.length;n++){var t=r[n];rudderanalytics[t]=function(r){return function(){var n
  ;Array.isArray(window[e])?rudderanalytics.push([r].concat(Array.prototype.slice.call(arguments))):null===(n=window[e][r])||void 0===n||n.apply(window[e],arguments)
  }}(t)}try{
  new Function('class Test{field=()=>{};test({prop=[]}={}){return prop?(prop?.property??[...prop]):import("");}}'),
  window.rudderAnalyticsBuildType="modern"}catch(i){}var d=document.head||document.getElementsByTagName("head")[0]
  ;var o=document.body||document.getElementsByTagName("body")[0];window.rudderAnalyticsAddScript=function(e,r,n){
  var t=document.createElement("script");t.src=e,t.setAttribute("data-loader","RS_JS_SDK"),r&&n&&t.setAttribute(r,n),
  "async"===scriptLoadingMode?t.async=true:"defer"===scriptLoadingMode&&(t.defer=true),
  d?d.insertBefore(t,d.firstChild):o.insertBefore(t,o.firstChild)},window.rudderAnalyticsMount=function(){!function(){
  if("undefined"==typeof globalThis){var e;var r=function getGlobal(){
  return"undefined"!=typeof self?self:"undefined"!=typeof window?window:null}();r&&Object.defineProperty(r,"globalThis",{
  value:r,configurable:true})}
  }(),window.rudderAnalyticsAddScript("".concat(sdkBaseUrl,"/").concat(sdkVersion,"/").concat(window.rudderAnalyticsBuildType,"/").concat(sdkFileName),"data-rsa-write-key","2SD2NDpXDbMno0VgTrd67mEErgs")
  },
  "undefined"==typeof Promise||"undefined"==typeof globalThis?window.rudderAnalyticsAddScript("https://polyfill-fastly.io/v3/polyfill.min.js?version=3.111.0&features=Symbol%2CPromise&callback=rudderAnalyticsMount"):window.rudderAnalyticsMount()
  ;var loadOptions={};rudderanalytics.load("${process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY}", "${process.env.NEXT_PUBLIC_RUDDERSTACK_DOMAIN}",loadOptions)}}}();
                `}
            </Script>
          )}
        <OrderDataProvider>
          <RemoteConfigProvider serverFlags={serverFlags}>
            <Component {...pageProps} err={err} />
          </RemoteConfigProvider>
        </OrderDataProvider>
      </AdDataProvider>
      <SpeedInsights />
    </>
  );
}

export default App;
